// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartlineContainerStyles } from "@conklin/App/Components/Cart/CartLineContainer";
import { color, getSystemColor, xScreen } from "@conklin/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { css } from "styled-components";

cartlineContainerStyles.container = {
    css: css`
        display: grid;

        ${({ includeActions }: any) =>
            includeActions
                ? "grid-template-columns: 91px minmax(45px,1fr) minmax(0,176px) minmax(0,129px) minmax(0,176px) minmax(34px, 110px);"
                : "grid-template-columns: 91px minmax(45px,1fr) minmax(0,176px) minmax(0,129px) minmax(0,176px);"};

        ${xScreen("lte", "lg")} {
            ${({ includeActions }: any) =>
                includeActions
                    ? "grid-template-columns: 91px minmax(45px,1fr) minmax(0,148px) minmax(0,108px) minmax(0,148px) minmax(34px, 110px);"
                    : "grid-template-columns: 91px minmax(45px,1fr) minmax(0,148px) minmax(0,108px) minmax(0,148px);"};
        }

        grid-column-gap: 12px;
        background: ${color("common.background")};
        padding: 12px 8px;
        width: 100%;
        border-bottom: 1px solid ${color("common.border")};

        ${xScreen("lte", "md")} {
            padding: 12px 0;
            ${({ includeActions }: any) =>
                includeActions
                    ? "grid-template-columns: 91px minmax(45px,1fr) minmax(0,148px) minmax(0,108px) minmax(0,148px) 28px"
                    : "grid-template-columns: 91px minmax(45px,1fr) minmax(0,148px) minmax(0,108px) minmax(0,148px);"};
        }

        @media print {
            width: 100%;
            content: "";
            display: inline-block;
            clear: both;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            border-bottom: 1px solid ${getSystemColor("common.border")};
        }
    `,
};
cartlineContainerStyles.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 5;
        ${xScreen("lte", "md")} {
            grid-row: 1 / 3;
            margin-bottom: 12px;
        }
        @media print {
            min-width: 100px;
            min-height: 100px;
            margin-bottom: 0;
            float: left;
        }
    `,
};
cartlineContainerStyles.description = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        position: relative;
        display: flex;
        flex-direction: column;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 6;
            grid-row: 1 / 3;
        }
        @media print {
            min-width: 75%;
            max-width: 75%;
            min-height: 100px;
            float: right;
            display: block;
        }
    `,
};
cartlineContainerStyles.inventory = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 6px;

        ${xScreen("lte", "md")} {
            grid-column: 1 / 6;
            grid-row: 3 / 4;
            padding-top: 0;
        }

        @media print {
            min-width: 25%;
            max-width: 25%;
            min-height: 50px;
            float: left;
        }
    `,
};

cartlineContainerStyles.qty = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 2;
            grid-row: 4 / 5;
            margin-right: 20px;
        }
        ${xScreen("lte", "sm")} {
            padding-bottom: 5px;
        }
        @media print {
            margin-right: 0;
            min-width: 40%;
            min-height: 50px;
            float: left;
        }
    `,
};
cartlineContainerStyles.total = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 4px;

        ${xScreen("lte", "md")} {
            grid-column: 4 / 6;
            grid-row: 4 / 5;
            padding-top: 0;
        }

        @media print {
            min-width: 35%;
            min-height: 50px;
            float: right;
        }
    `,
};

cartlineContainerStyles.remove = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        padding: 6px 4px 0 0;

        ${StyledButton} {
            float: right;
        }

        ${xScreen("lte", "md")} {
            padding: 0;
            grid-row: 1 / 6;
        }

        ${xScreen("lte", "sm")} {
            grid-row: 1 / 7;
        }
    `,
};

cartlineContainerStyles.actions = {
    css: css`
        grid-column: 1 / 3;
        grid-row: 2 / 3;
        position: relative;
        display: flex;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 6;
            grid-row: 5 / 6;
        }
        @media print {
            display: none;
        }
    `,
};

cartlineContainerStyles.notes = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 4 / 5;
        position: relative;
        padding-top: 8px;

        ${xScreen("lte", "md")} {
            grid-column: 1 / 6;
            grid-row: 5 / 6;
            padding-top: 12px;
        }

        ${xScreen("lte", "sm")} {
            grid-column: 1 / 7;
        }

        @media print {
            min-width: 100%;
            float: left;
        }
        &:empty {
            display: none;
        }
    `,
};
